import type { QueryClient } from "@tanstack/react-query";

import type { Exercise, FeedbackContext } from "../api/types";
import { useEnvironment } from "../app/environment";
import { CLIENT_BUILD } from "../app/versionCheck";
import { lang } from "../i18n/i18n";
import { useUi } from "../state/ui";
import { recentErrors } from "./errorLog";

/** Contexte technique d'un retour (registre F39), montré à l'utilisateur avant l'envoi : vue ouverte, dossier,
 *  niveau et mission, version, langue, navigateur, écran, dernières erreurs. Jamais de licence, de code, de cookie
 *  ni de contenu du dossier au-delà de son nom. */

export interface ContextInput {
  welcome: boolean;
  view: string;
  objectsFilter?: string | null;
  exercise?: Pick<Exercise, "id" | "name" | "map_slug"> & { parameters?: { level?: string | null; mission?: string | null } | null };
  language: "fr" | "en";
  clientBuild: string;
  environment?: string;
  userAgent: string;
  platform?: string;
  screen: { width: number; height: number };
  viewport: { width: number; height: number };
  pixelRatio: number;
  touch: boolean;
  errors: FeedbackContext["errors"];
}

const size = (s: { width: number; height: number }) => `${Math.round(s.width)}×${Math.round(s.height)}`;

/** Contexte à joindre, d'après l'état donné (fonction pure, essayée seule). */
export function buildContext(input: ContextInput): FeedbackContext {
  const context: FeedbackContext = {
    welcome: input.welcome,
    view: input.welcome ? "accueil" : input.view,
    language: input.language,
    client_build: input.clientBuild,
    user_agent: input.userAgent.slice(0, 300),
    screen: size(input.screen),
    viewport: size(input.viewport),
    pixel_ratio: Math.round(input.pixelRatio * 100) / 100,
    touch: input.touch,
  };
  if (input.environment) context.environment = input.environment;
  if (input.platform) context.platform = input.platform.slice(0, 60);
  if (!input.welcome && input.view === "objects" && input.objectsFilter) context.panel = input.objectsFilter.toLowerCase();
  // Accueil : le dernier dossier ouvert n'est pas celui qu'on regarde, il n'est pas joint.
  if (input.exercise && !input.welcome) {
    context.exercise_id = input.exercise.id;
    context.exercise_name = input.exercise.name.slice(0, 120);
    if (input.exercise.map_slug) context.map = input.exercise.map_slug;
    const level = input.exercise.parameters?.level;
    const mission = input.exercise.parameters?.mission;
    if (level) context.level = level;
    if (mission) context.mission = mission;
  }
  if (input.errors?.length) context.errors = input.errors.slice(-10);
  return context;
}

/** Contexte de l'instant (état de l'interface, dossier ouvert lu dans le cache des dossiers, navigateur). */
export function currentContext(queryClient: QueryClient | null): FeedbackContext {
  const ui = useUi.getState();
  const exercises = queryClient?.getQueryData<Exercise[]>(["exercises"]) ?? [];
  const exercise = ui.exerciseId ? exercises.find((e) => e.id === ui.exerciseId) : undefined;
  const nav = typeof navigator === "undefined" ? undefined : navigator;
  const win = typeof window === "undefined" ? undefined : window;
  return buildContext({
    welcome: ui.welcome,
    view: ui.view,
    objectsFilter: ui.objectsFilter,
    exercise,
    language: lang(),
    clientBuild: CLIENT_BUILD,
    environment: useEnvironment.getState().environment,
    userAgent: nav?.userAgent ?? "",
    platform: (nav as (Navigator & { userAgentData?: { platform?: string } }) | undefined)?.userAgentData?.platform ?? nav?.platform,
    screen: { width: win?.screen?.width ?? 0, height: win?.screen?.height ?? 0 },
    viewport: { width: win?.innerWidth ?? 0, height: win?.innerHeight ?? 0 },
    pixelRatio: win?.devicePixelRatio ?? 1,
    touch: Boolean(nav && (nav.maxTouchPoints ?? 0) > 0),
    errors: recentErrors(),
  });
}
